import React from 'react';
import { useBattle } from '../context';

/**
 * SelectionOverlay コンポーネント
 *
 * 手札選択・モンスターターゲット選択のオーバーレイUI
 * - pendingHandSelection: 手札からカードを選択するモード
 * - pendingMonsterTarget: フィールドのモンスターを選択するモード
 */
const SelectionOverlay: React.FC = () => {
  const {
    currentPlayer,
    isMobile,
    p1Field,
    p2Field,
    pendingHandSelection,
    pendingSelectedCard,
    pendingMonsterTarget,
    pendingSelectedMonsterIndex,
    setPendingMonsterTarget,
    setPendingSelectedMonsterIndex,
    confirmHandSelection,
  } = useBattle();

  // 手札選択モードUI
  const renderHandSelectionOverlay = () => {
    if (!pendingHandSelection) return null;

    return (
      <div
        onClick={pendingSelectedCard ? confirmHandSelection : undefined}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: pendingSelectedCard
            ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
            : 'linear-gradient(135deg, #ff6b6b 0%, #ff8e53 100%)',
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: pendingSelectedCard
            ? '0 4px 20px rgba(76,175,80,0.4)'
            : '0 4px 20px rgba(255,107,107,0.4)',
          cursor: pendingSelectedCard ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          zIndex: 100,
        }}
      >
        <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}>
          {pendingHandSelection.message}
        </div>
        {pendingSelectedCard ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
            <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#fff' }}>
              選択中: <strong>{pendingSelectedCard.name}</strong>
            </div>
            <div style={{ fontSize: '12px', color: '#e0ffe0' }}>
              クリックで決定
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: '#ffe0e0' }}>
            手札からカードをクリックして選択してください
          </div>
        )}
      </div>
    );
  };

  // モンスターターゲット選択モードUI
  const renderMonsterTargetOverlay = () => {
    if (!pendingMonsterTarget) return null;

    const handleConfirm = () => {
      if (pendingSelectedMonsterIndex !== null) {
        pendingMonsterTarget.callback(pendingSelectedMonsterIndex);
        setPendingMonsterTarget(null);
        setPendingSelectedMonsterIndex(null);
      }
    };

    // 選択中のモンスター名を取得
    const getSelectedMonsterName = () => {
      if (pendingSelectedMonsterIndex === null) return null;
      const targetField = pendingMonsterTarget.targetPlayer === 'opponent'
        ? (currentPlayer === 1 ? p2Field : p1Field)
        : (currentPlayer === 1 ? p1Field : p2Field);
      return targetField[pendingSelectedMonsterIndex]?.name;
    };

    return (
      <div
        onClick={pendingSelectedMonsterIndex !== null ? handleConfirm : undefined}
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: pendingSelectedMonsterIndex !== null
            ? 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)'
            : 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
          padding: isMobile ? '12px 16px' : '16px 24px',
          borderRadius: '12px',
          textAlign: 'center',
          boxShadow: pendingSelectedMonsterIndex !== null
            ? '0 4px 20px rgba(76,175,80,0.4)'
            : '0 4px 20px rgba(233,30,99,0.4)',
          cursor: pendingSelectedMonsterIndex !== null ? 'pointer' : 'default',
          transition: 'all 0.2s ease',
          zIndex: 100,
        }}
      >
        <div style={{ fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', color: '#fff', marginBottom: '8px' }}>
          {pendingMonsterTarget.message}
        </div>
        {pendingSelectedMonsterIndex !== null ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'center' }}>
            <div style={{ fontSize: isMobile ? '12px' : '14px', color: '#fff' }}>
              選択中: <strong>{getSelectedMonsterName()}</strong>
            </div>
            <div style={{ fontSize: '12px', color: '#e0ffe0' }}>
              クリックで決定
            </div>
          </div>
        ) : (
          <div style={{ fontSize: '12px', color: '#ffe0e0' }}>
            {pendingMonsterTarget.targetPlayer === 'opponent' ? '相手' : '自分'}のフィールドからモンスターを選択
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {renderHandSelectionOverlay()}
      {renderMonsterTargetOverlay()}
    </>
  );
};

export default SelectionOverlay;
